import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Grid, CircularProgress, Container, Button } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useSelector, useDispatch } from 'react-redux';

import { getCurrentProfile } from '../../../actions/profile';
import { urlImg } from '../../../components/common/helpers';

import PageBanner from '../../../components/services/page/PageBanner';
import PageSummary from '../../../components/services/page/PageSummary';
import PageInfoBar from '../../../components/services/page/PageInfoBar';
import PageLocation from '../../../components/services/page/PageLocation';
import PageComments from '../../../components/services/page/PageComments';
import PageServiceList from '../../../components/services/page/PageServiceList';

const UserPageDetail = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const profile = useSelector(state => state.profile.profile);
  const loading = useSelector(state => state.profile.loading);
  const categories = useSelector(state => state.cat.categories);
  const currentUser = useSelector(state => state.user.user);

  const [page, setPage] = useState(null);
  const [resolvedServices, setResolvedServices] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!profile || Object.keys(profile).length === 0) {
      dispatch(getCurrentProfile());
    }
  }, [dispatch, profile]);

  useEffect(() => {
    if (profile && profile.paginas && !loading) {
      const foundPage = profile.paginas.find(p => p._id.toString() === pageId.toString());
      if (foundPage) {
        setPage(foundPage);

        // Resolve services from the profile's services list
        if (foundPage.servicios && profile.servicios) {
          const matched = foundPage.servicios.map(ps => {
            const serviceId = ps.servicio?._id || ps.servicio || ps;
            const fullService = profile.servicios.find(s => s._id.toString() === serviceId.toString());
            if (fullService) {
              // Populate facilitator data from page members if available
              const facilitatorsData = (ps.facilitadores || (ps.facilitador ? [ps.facilitador] : [])).map(fId => {
                const member = foundPage.miembros.find(m => (m.usuario?._id || m._id) === fId.toString());
                if (member && member.usuario) return member.usuario;
                if (member) return member;
                return null;
              }).filter(f => f);

              return {
                ...fullService,
                facilitadores: facilitatorsData
              };
            }
            return null;
          }).filter(s => s !== null && !s.borrador && s.status);
          setResolvedServices(matched);
        }
      } else {
        // If not found in own profile, maybe redirect or handle error
        // navigate('/user/servicios', { replace: true });
      }
    }
  }, [profile, pageId, loading]);

  const categoriaName = useMemo(() => {
    if (!page?.categoria) return "";
    const found = categories?.find(cat => cat._id === page.categoria);
    return found ? found.name : "";
  }, [page?.categoria, categories]);

  const addressInfo = useMemo(() => {
    if (!page?.ubicacion) return null;
    const found = profile?.direcciones?.find(ad => ad._id?.toString() === page.ubicacion?.toString());
    return found || null;
  }, [page?.ubicacion, profile?.direcciones]);

  if (loading || !page) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F8F9FA' }}>
        <CircularProgress />
      </Box>
    );
  }

  const bannerUrl = page.img ? `${urlImg}/paginas/${page._id}/${page.img}` : 'https://images.unsplash.com/photo-1555507036-ab1f4038808a?q=80&w=1926&auto=format&fit=crop';

  // Resolve user avatar from currentUser or profile
  const ownerUser = currentUser || profile.user;
  let ownerAvatarUrl = "";
  if (ownerUser?.avatar) {
    ownerAvatarUrl = ownerUser.avatar.startsWith('http')
      ? ownerUser.avatar
      : `${urlImg}/users/${ownerUser._id}/${ownerUser.avatar}`;
  } else if (profile.avatar) {
    ownerAvatarUrl = `${urlImg}/perfiles/${profile._id}/${profile.avatar}`;
  }

  // Calificación de la página = promedio ponderado de los servicios listados (por nº de reseñas)
  const serviciosCalificados = (resolvedServices || []).filter(s => s.ratingPromedio > 0 && s.ratingTotal > 0);
  const totalResenas = serviciosCalificados.reduce((acc, s) => acc + s.ratingTotal, 0);
  const ratingPagina = totalResenas > 0
    ? serviciosCalificados.reduce((acc, s) => acc + s.ratingPromedio * s.ratingTotal, 0) / totalResenas
    : 0;

  return (
    <Box sx={{ bgcolor: '#F8F9FA', minHeight: '100vh', pb: 8, pt: 4, mt: 10 }}>
      <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2, md: 4 } }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIosIcon />}
          onClick={() => navigate(-1)}
          sx={{
            textTransform: 'none',
            color: '#0E2465',
            fontWeight: 'bold',
            mb: 2,
            ml: { xs: 2, md: 0 },
            '&:hover': { bgcolor: 'transparent', opacity: 0.8 }
          }}
        >
          Atrás
        </Button>

        <PageBanner
          title={page.nombre}
          description={page.descripcion}
          image={bannerUrl}
          isDraft={page.borrador}
        />

        <PageSummary
          title={page.nombre}
          rating={ratingPagina}
          ownerAvatar={ownerAvatarUrl}
          ownerName={`${ownerUser?.name || profile?.nombre || ""} ${ownerUser?.apellido || ""}`}
          ownerId={ownerUser?._id}
        />

        <PageInfoBar
          categories={categoriaName}
          redes={page.redes}
        />

        <Box sx={{ mt: 6, px: { xs: 2, md: 0 } }}>
          <Grid container spacing={5}>
            <Grid item size={{ xs: 12, md: 4.5 }}>
              {addressInfo && addressInfo.direccion && (
                <PageLocation
                  address={addressInfo.direccion}
                  placeId={addressInfo.place_id}
                />
              )}
              <PageComments
                proveedorId={ownerUser?._id}
                providerName={ownerUser?.name || profile?.nombre || "el profesional"}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 7.5 }}>
              <PageServiceList
                services={resolvedServices}
                ownerName={page.nombre}
                irA={navigate}
                ownerId={ownerUser?._id}
                categories={categories}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default UserPageDetail;
